import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AgenticInsightsApi } from '@/features/agentic-insights/api/agenticInsightsApi';
import { agenticInsightsQueryKeys } from '@/features/agentic-insights/queryKeys';
import { useAgenticRunDataSource } from './agenticRunDataSource';
import type { SignalScope } from './types';

/**
 * Starts a run and hands observation straight back to useAgenticRun: the invalidation pulls in the
 * `running` stub the backend stored, which is what starts the poll.
 *
 * It runs on settle rather than on success because a rejected POST does not mean no run started — a
 * timeout or a dropped connection can lose the response of a request the backend already accepted.
 * Re-reading either way lets the page discover that run instead of insisting nothing happened.
 */
export const useTriggerAgenticRun = (loopId: number) => {
  const queryClient = useQueryClient();
  const { dataSource } = useAgenticRunDataSource();

  return useMutation({
    mutationFn: (signalScope: SignalScope) => AgenticInsightsApi.triggerRun(loopId, signalScope),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: agenticInsightsQueryKeys.latestRun.detail(dataSource, loopId),
      });
      // The picker's list otherwise keeps showing the pre-trigger set of runs until something else
      // happens to refetch it — a new run existing is exactly the thing this invalidation misses.
      queryClient.invalidateQueries({
        queryKey: agenticInsightsQueryKeys.list.detail(dataSource, loopId),
      });
    },
  });
};
